import type {
  AdminChallengeRow,
  AdminGroupRow,
  AdminPrizeInput,
  ChallengeDetailResponse,
  CreateChallengeInput,
  CreateGroupInput,
  GroupBoardPeriod,
  GroupBoardResponse,
  GroupBoardTab,
  GroupCollectionBoardResponse,
  GroupCollectionsResponse,
  GroupDetailResponse,
  GroupJoinPreviewResponse,
  GroupJoinResponse,
  GroupMute,
  GroupRecentResponse,
  GroupsResponse,
  UpdateGroupInput,
  UpdateGroupMemberInput,
} from "@atlas/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "../lib/api-client";
import type { FeedResponse } from "./social";

// Groups (spec 32 / ATL-151). Paths mirror packages/shared/src/api/groups.ts
// (canonical) and packages/api/src/routes/groups.ts. Group posts are created
// through createPost in api/social.ts with { visibility: "group", groupId }.

export type {
  AdminChallengeRow,
  AdminGroupRow,
  AdminPrizeInput,
  BoardRow,
  ChallengeDetailResponse,
  ChallengePrizeStatus,
  ChallengeState,
  ChallengeSummary,
  ChallengeTargetType,
  ChallengeWinner,
  CreateChallengeInput,
  GroupBoardPeriod,
  GroupBoardTab,
  GroupCollectionBoardResponse,
  GroupCollectionSummary,
  GroupCollectionsResponse,
  GroupDetail,
  GroupDetailResponse,
  GroupListItem,
  GroupMember,
  GroupMemberIdentity,
  GroupMute,
  GroupRecentItem,
  GroupRole,
} from "@atlas/shared";

const enc = encodeURIComponent;

export function fetchGroups(): Promise<GroupsResponse> {
  return apiGet("/groups");
}

export function createGroup(input: CreateGroupInput): Promise<{ groupId: string }> {
  return apiPost("/groups", input);
}

export function fetchJoinPreview(code: string): Promise<GroupJoinPreviewResponse> {
  return apiGet(`/groups/join/${enc(code)}`);
}

/** `ref` is the inviting member's username from ?ref=, for referral attribution of new sign-ups. */
export function joinGroup(code: string, ref?: string | null): Promise<GroupJoinResponse> {
  return apiPost(`/groups/join/${enc(code)}`, ref ? { ref } : {});
}

export function fetchGroup(id: string): Promise<GroupDetailResponse> {
  return apiGet(`/groups/${enc(id)}`);
}

export function fetchGroupBoard(id: string, tab: GroupBoardTab, period: GroupBoardPeriod): Promise<GroupBoardResponse> {
  return apiGet(`/groups/${enc(id)}/board?tab=${tab}&period=${period}`);
}

/** All-time standings per collection, most members with progress first. */
export function fetchGroupCollections(id: string): Promise<GroupCollectionsResponse> {
  return apiGet(`/groups/${enc(id)}/collections`);
}

/** One collection's all-time board: every active member, zeros unranked at the bottom. */
export function fetchGroupCollectionBoard(id: string, slug: string): Promise<GroupCollectionBoardResponse> {
  return apiGet(`/groups/${enc(id)}/collections/${enc(slug)}`);
}

export function fetchGroupRecent(id: string): Promise<GroupRecentResponse> {
  return apiGet(`/groups/${enc(id)}/recent`);
}

/** Same post shape as the main feed, typed with web's SocialPost so PostCard renders it. */
export function fetchGroupFeed(id: string, cursor?: string): Promise<FeedResponse> {
  return apiGet(
    cursor ? `/groups/${enc(id)}/feed?cursor=${enc(cursor)}` : `/groups/${enc(id)}/feed`,
  );
}

export function updateGroup(id: string, input: UpdateGroupInput): Promise<{ ok: true }> {
  return apiPatch(`/groups/${enc(id)}`, input);
}

export function regenerateInviteCode(id: string): Promise<{ inviteCode: string }> {
  return apiPost(`/groups/${enc(id)}/invite-code`);
}

export function updateGroupMember(id: string, userId: string, input: UpdateGroupMemberInput): Promise<{ ok: true }> {
  return apiPatch(`/groups/${enc(id)}/members/${enc(userId)}`, input);
}

export function leaveGroup(id: string): Promise<{ ok: true }> {
  return apiDelete(`/groups/${enc(id)}/members/me`);
}

export function deleteGroup(id: string): Promise<{ ok: true }> {
  return apiDelete(`/groups/${enc(id)}`);
}

export function setGroupMute(id: string, mute: GroupMute): Promise<{ ok: true; mute: GroupMute }> {
  return apiPatch(`/groups/${enc(id)}/me`, { mute });
}

export function reportGroup(id: string, reason: string): Promise<{ ok: true }> {
  return apiPost(`/groups/${enc(id)}/report`, { reason });
}

export function createChallenge(groupId: string, input: CreateChallengeInput): Promise<{ challengeId: string }> {
  return apiPost(`/groups/${enc(groupId)}/challenges`, input);
}

export function fetchChallenge(groupId: string, challengeId: string): Promise<ChallengeDetailResponse> {
  return apiGet(`/groups/${enc(groupId)}/challenges/${enc(challengeId)}`);
}

/** Signed link to the caller's winner share card (OG page; add .png for the image). */
export function fetchChallengeShareUrl(groupId: string, challengeId: string): Promise<{ url: string }> {
  return apiGet(`/groups/${enc(groupId)}/challenges/${enc(challengeId)}/share-url`);
}

export function cancelChallenge(groupId: string, challengeId: string): Promise<{ ok: true }> {
  return apiDelete(`/groups/${enc(groupId)}/challenges/${enc(challengeId)}`);
}

// ─── Atlas admin ──────────────────────────────────────────────────────────────

export function fetchAdminGroups(q?: string, opts: { reported?: boolean; deleted?: boolean } = {}): Promise<{ items: AdminGroupRow[] }> {
  const params = new URLSearchParams();
  const query = q?.trim();
  if (query) params.set("q", query);
  if (opts.reported) params.set("reported", "1");
  if (opts.deleted) params.set("deleted", "1");
  const qs = params.toString();
  return apiGet(qs ? `/admin/groups?${qs}` : "/admin/groups");
}

export function adminDeleteGroup(id: string): Promise<{ ok: true }> {
  return apiDelete(`/admin/groups/${enc(id)}`);
}

export function fetchAdminChallenges(prize: "requested" | "approved"): Promise<{ items: AdminChallengeRow[] }> {
  return apiGet(`/admin/challenges?prize=${prize}`);
}

export function adminSetChallengePrize(challengeId: string, input: AdminPrizeInput): Promise<{ ok: true }> {
  return apiPost(`/admin/challenges/${enc(challengeId)}/prize`, input);
}

export function adminGrantChallengePrize(challengeId: string): Promise<{ ok: true }> {
  return apiPost(`/admin/challenges/${enc(challengeId)}/grant`);
}

export function adminRejectChallengePrize(challengeId: string): Promise<{ ok: true }> {
  return apiPost(`/admin/challenges/${enc(challengeId)}/reject`);
}

/** Presigned PUT for a group image (under the caller's groups/ prefix). Create and PATCH accept the returned publicUrl only. */
export function getGroupImageUploadUrl(contentType: string): Promise<{ uploadUrl: string; key: string; publicUrl: string }> {
  return apiPost("/groups/image-upload-url", { contentType });
}

export interface AdminPrizeItem {
  id: string;
  slug: string;
  name: string;
  kind: string;
  rarity: string;
}

/** The only items the prize picker may offer: active, priced cosmetics (server-filtered). */
export function fetchAdminPrizeItems(): Promise<{ items: AdminPrizeItem[] }> {
  return apiGet("/admin/challenges/prize-items");
}
